import { ref } from 'vue'
import { sanitizePackCounts, type AccountRewardPayload } from '@shared/index'
import { api } from '../api/http'
import { useAuthStore } from '../stores/auth'
import { useCollectionStore } from '../stores/collection'
import { useWalletStore } from '../stores/wallet'

/** Coins to explain in the toast. 0 hides that part. */
export const staffGrantNoticeCoins = ref(0)
/** Master Packs to explain in the toast. 0 hides that part. */
export const staffGrantNoticePacks = ref(0)

let inflight: Promise<void> | null = null
let claimed = false
let acked = false

export function clearStaffGrantNotice(): void {
  staffGrantNoticeCoins.value = 0
  staffGrantNoticePacks.value = 0
  claimed = false
  acked = false
}

/**
 * Applies a live gift: balances update and the toast explains this grant.
 */
export function applyStaffReward(raw: AccountRewardPayload): void {
  const coins = Number.isInteger(raw?.coins) && raw.coins > 0 ? raw.coins : 0
  const packs = Number.isInteger(raw?.packs) && raw.packs > 0 ? raw.packs : 0
  const balance = raw?.balance
  if (typeof balance === 'number' && Number.isFinite(balance)) {
    useWalletStore().setCoins(balance)
  }
  if (raw?.packCounts && typeof raw.packCounts === 'object') {
    useCollectionStore().applyPacks(sanitizePackCounts(raw.packCounts))
  }
  if (coins < 1 && packs < 1) return
  claimed = true
  acked = false
  staffGrantNoticeCoins.value += coins
  staffGrantNoticePacks.value += packs
  void ackStaffGrantNotice()
}

/**
 * Loads unread staff gifts, updates coins and packs, and arms the toast.
 * A miss stays unclaimed so a later visit can try again.
 */
export function pullStaffGrantNotice(): Promise<void> {
  if (claimed) return Promise.resolve()
  if (inflight) return inflight
  inflight = loadNotice().finally(() => {
    inflight = null
  })
  return inflight
}

async function loadNotice(): Promise<void> {
  const token = useAuthStore().token
  if (!token) return
  const collection = useCollectionStore()
  const epochAtFetch = collection.mutationEpoch()
  try {
    const body = await api<AccountRewardPayload>('/account/staff-grant-notice', {
      token,
    })
    const coins = Number.isInteger(body.coins) && body.coins > 0 ? body.coins : 0
    const packs = Number.isInteger(body.packs) && body.packs > 0 ? body.packs : 0
    if (coins < 1 && packs < 1) return
    if (collection.mutationEpoch() !== epochAtFetch) return
    claimed = true
    acked = false
    if (typeof body.balance === 'number') useWalletStore().setCoins(body.balance)
    collection.applyPacks(sanitizePackCounts(body.packCounts))
    staffGrantNoticeCoins.value += coins
    staffGrantNoticePacks.value += packs
  } catch {
    /* A later visit can try again. */
  }
}

/** Marks the notice read. Safe to call more than once. */
export async function ackStaffGrantNotice(): Promise<void> {
  if (acked) return
  acked = true
  const token = useAuthStore().token
  if (!token) return
  try {
    await api('/account/staff-grant-notice/ack', { method: 'POST', token })
  } catch {
    acked = false
  }
}
